import { isMediaSourceSupported } from '@studio/utils/ad/browser-feature-support';
import { ManifestManager } from './manifest/manifest-manager';
import { MediaStreamer } from './media-streamer';
import { StreamingHttpClient } from './streaming-http-client';
export class StreamManager {
    constructor(videoElement, manifestUrl) {
        this._videoCanPlayThrough = false;
        this._audioCanPlayThrough = false;
        this._audioHasPlayed = false;
        this._httpClient = new StreamingHttpClient();
        this._manifestUrl = manifestUrl;
        this._manifestManager = new ManifestManager(this._httpClient);
        this._videoElement = videoElement;
        this._videoStreamer = new MediaStreamer(this._videoElement, 'video', this._manifestManager, this._httpClient);
    }
    async loadStreams_m() {
        // check for bare minimum browser support
        if (!isMediaSourceSupported()) {
            throw new Error('MediaSource not supported');
        }
        await this._manifestManager.init_m(this._manifestUrl);
        const hasAudio = !!this._manifestManager.getCurrentRepresentation_m('audio');
        if (hasAudio) {
            this._audioElement = document.createElement('audio');
            this._audioElement.muted = true;
            this._audioElement.disableRemotePlayback = true; // Required for Apple to play streaming with ManagedMediaSource
            this._audioStreamer = new MediaStreamer(this._audioElement, 'audio', this._manifestManager, this._httpClient);
            this._audioStreamer.on_m('chunksLoaded', () => {
                this._audioCanPlayThrough = true;
            });
            this._videoStreamer.on_m('chunksLoaded', () => {
                this._videoCanPlayThrough = true;
            });
            this._audioElement.src = this._initAudioStream();
        }
        return this._initVideoStream(); // we only need to return the video source, audio is controlled here for now
    }
    destroy_m() {
        var _a;
        this._videoStreamer.destroy_m();
        (_a = this._audioStreamer) === null || _a === void 0 ? void 0 : _a.destroy_m();
        this._audioStreamer = undefined;
        if (this._audioElement) {
            this._audioElement.pause();
            this._audioElement.removeAttribute('src');
            this._audioElement.load();
            this._audioElement = undefined;
        }
    }
    toggleAudioMute_m(muted) {
        if (this._audioElement) {
            this._audioElement.muted = muted;
            if (!muted && !this._audioHasPlayed) {
                this._audioHasPlayed = true;
                this._audioElement.currentTime = this._videoElement.currentTime;
                this._audioElement.play();
                this._syncStreams(this._videoElement, this._audioElement);
            }
        }
    }
    _initVideoStream() {
        const videoCodecs = this._manifestManager.getCodecs_m('video');
        const videoMimeType = `video/mp4; codecs="${videoCodecs}"`;
        this._validateStreamingCapabilities(videoMimeType);
        return this._videoStreamer.init_m();
    }
    _initAudioStream() {
        const audioCodecs = this._manifestManager.getCodecs_m('audio');
        const audioMimeType = `audio/mp4; codecs="${audioCodecs}"`;
        this._validateStreamingCapabilities(audioMimeType);
        return this._audioStreamer.init_m();
    }
    _validateStreamingCapabilities(mimeCodec) {
        const isStreamingSupported = this._isSupported(mimeCodec);
        if (!isStreamingSupported) {
            throw new Error(`Streaming '${mimeCodec}' is not supported`);
        }
    }
    _isSupported(mimeCodec) {
        if ('ManagedMediaSource' in window) {
            return window.ManagedMediaSource.isTypeSupported(mimeCodec);
        }
        return 'MediaSource' in window && MediaSource.isTypeSupported(mimeCodec);
    }
    /**
     * Syncs the video and audio streams
     * Video stream controls the audio stream (play, pause, seek)
     * When video or audio is waiting, the other stream is paused
     * @param videoElement
     * @param audioElement
     */
    _syncStreams(videoElement, audioElement) {
        if (!audioElement)
            return;
        const onVideoPause = () => {
            if (!audioElement.paused) {
                audioElement.pause();
            }
        };
        const onVideoPlay = () => {
            if (audioElement.paused) {
                audioElement.play();
            }
        };
        const onVideoSeeked = () => {
            audioElement.currentTime = videoElement.currentTime;
        };
        const onVideoWaiting = () => {
            if (this._videoCanPlayThrough) {
                this._videoElement.removeEventListener('waiting', onVideoWaiting);
                return;
            }
            if (!audioElement.paused) {
                audioElement.pause();
            }
        };
        const onVideoCanPlay = () => {
            var _a;
            if (this._videoCanPlayThrough) {
                (_a = this._videoElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('canplay', onVideoCanPlay);
                return;
            }
            console.debug('[Stream Manager] Video can play');
            if (audioElement.paused) {
                audioElement.play();
            }
        };
        const onAudioWaiting = () => {
            var _a;
            if (this._audioCanPlayThrough) {
                (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('waiting', onAudioWaiting);
                return;
            }
            console.debug('[Stream Manager] Waiting for audio chunk(s)');
            if (!videoElement.paused) {
                videoElement.pause();
            }
        };
        const onAudioCanPlay = () => {
            var _a;
            if (this._audioCanPlayThrough) {
                (_a = this._audioElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('canplay', onAudioCanPlay);
                return;
            }
            if (videoElement.paused) {
                videoElement.play();
            }
        };
        videoElement.addEventListener('pause', onVideoPause);
        videoElement.addEventListener('play', onVideoPlay);
        videoElement.addEventListener('seeked', onVideoSeeked);
        videoElement.addEventListener('waiting', onVideoWaiting);
        videoElement.addEventListener('canplay', onVideoCanPlay);
        audioElement.addEventListener('waiting', onAudioWaiting);
        audioElement.addEventListener('canplay', onAudioCanPlay);
    }
}
